<template>
  <section class="section-base form-base form-view-result">
    <template v-if="loaded">
      <div v-if="hasTitle" class="section-base__title-box">
        <div class="section-base__title">{{ title }}</div>
      </div>

      <component :is="service" :statement="statementInfoToShow" />
      <Documents
        v-if="statementInfoToShow.documents"
        class="mt-10"
        :documents="statementInfoToShow.documents"
      />
    </template>

    <SkeletonLoader v-if="loading" />
  </section>
</template>

<script>
import { mapStatementName } from '@/utils/services';
import SkeletonLoader from '@/components/base/SkeletonLoader';
import { StatementInfoToShowModule } from '@/store/modules/statementInfoToShow';
import { StatementStatusInfoModule } from '@/store/modules/statementStatusInfo';
import { StatementType } from '@/types/services';
import NoticeRecovery from './views/NoticeRecovery';
import RUZDI from './views/RUZDI';
import SecuringEvidence from './views/SecuringEvidence';
import TransferElDocs from './views/TransferElDocs';
import AcceptanceDeposit from './views/AcceptanceDeposit';
import CorrectTranslation from './views/CorrectTranslation';
import AcceptanceEscrow from './views/AcceptanceEscrow';
import StorageAccept from './views/StorageAccept';
import StorageIssue from './views/StorageIssue';
import DocumentConversion from './views/DocumentConversion';
import ROD from './views/ROD';
import Documents from '@/components/elements/statement/FullStatementView/elements/Documents';
import { EditedStatementModule } from '@/store/modules/alternativeServices/editedStatement';
import ExtractionRod from './views/ExtractionRod';
import EgrnConfirm from '@/components/elements/statement/FullStatementView/views/EgrnConfirm';

const Service = {
  [StatementType.NOTICE_RECOVERY]: NoticeRecovery,
  [StatementType.RUZDI]: RUZDI,
  [StatementType.SECURING_EVIDENCE]: SecuringEvidence,
  [StatementType.TRANSFER_EL_DOCS]: TransferElDocs,
  [StatementType.ACCEPTANCE_DEPOSIT]: AcceptanceDeposit,
  [StatementType.CORRECT_TRANSLATION]: CorrectTranslation,
  [StatementType.ACCEPTANCE_ESCROW]: AcceptanceEscrow,
  [StatementType.STORAGE_ACCEPT]: StorageAccept,
  [StatementType.STORAGE_ISSUE]: StorageIssue,
  [StatementType.DOCUMENT_CONVERSION]: DocumentConversion,
  [StatementType.ROD]: ROD,
  [StatementType.EXTRACTION_ROD]: ExtractionRod,
  [StatementType.EGRN_CONFIRM]: EgrnConfirm,
};

export default {
  name: 'FormView',
  components: {
    Documents,
    SkeletonLoader,
  },
  props: {
    statementId: {
      type: String,
    },
    originalStatementId: {
      type: String,
    },
    hasTitle: {
      type: Boolean,
    },
    isAlternativeServiceEdit: {
      type: Boolean,
    },
  },
  computed: {
    id() {
      return this.isAlternativeServiceEdit
        ? this.originalStatementId
        : this.statementId;
    },

    getStatementInfoToShowService() {
      return this.isAlternativeServiceEdit
        ? EditedStatementModule.getStatementInfoToShow
        : StatementInfoToShowModule.getStatementInfoToShow;
    },

    statementInfoToShow() {
      return this.isAlternativeServiceEdit
        ? EditedStatementModule.statementInfoToShow.entity
        : StatementInfoToShowModule.entity;
    },

    statementInfoToShowLoading() {
      return this.isAlternativeServiceEdit
        ? EditedStatementModule.statementInfoToShow.loading
        : StatementInfoToShowModule.loading;
    },

    statementInfoToShowLoaded() {
      return this.isAlternativeServiceEdit
        ? EditedStatementModule.statementInfoToShow.loaded
        : StatementInfoToShowModule.loaded;
    },

    statementStatusInfo() {
      return StatementStatusInfoModule.entity;
    },

    loading() {
      if (!this.hasTitle) return this.statementInfoToShowLoading;

      return (
        this.statementInfoToShowLoading || StatementStatusInfoModule.loading
      );
    },

    loaded() {
      if (!this.hasTitle) return this.statementInfoToShowLoaded;

      return this.statementInfoToShowLoaded && StatementStatusInfoModule.loaded;
    },

    title() {
      const serviceName =
        mapStatementName[this.statementInfoToShow.statementType];

      return this.statementStatusInfo.extId
        ? `${serviceName} ${this.statementStatusInfo.extId}`
        : serviceName;
    },

    service() {
      return Service[this.statementInfoToShow.statementType];
    },
  },
  created() {
    this.getData();
  },
  methods: {
    getData() {
      this.getStatementInfoToShowService(this.id);

      if (this.hasTitle) {
        StatementStatusInfoModule.get(this.id);
      }
    },
  },
};
</script>

<style lang="scss">
.form-view-result {
  & .v-input--is-readonly {
    .v-label {
      color: #c8c5c5 !important;
    }

    input,
    textarea {
      color: #262626 !important;
      font-size: 16px !important;
      cursor: auto;
    }

    textarea {
      line-height: 20px;
      padding-top: 5px;
      padding-bottom: 5px;
    }

    & > .v-input__control > .v-input__slot:after {
      transform: scaleX(0) !important;
    }

    &::v-deep {
      .v-label {
        color: #c8c5c5 !important;
      }

      input,
      textarea {
        color: #262626 !important;
        font-size: 16px !important;
        cursor: auto;
      }

      textarea {
        line-height: 20px;
        padding-top: 5px;
        padding-bottom: 5px;
      }

      & > .v-input__control > .v-input__slot:after {
        transform: scaleX(0) !important;
      }
    }
  }

  & .preview {
    margin-top: 20px;

    & .break {
      flex-basis: 100%;
      width: 100% !important;
      max-width: 100% !important;
    }
  }

  & .row-box {
    display: flex;
    flex-wrap: wrap;
    width: 100%;
    align-items: center;

    & .input-raw {
      padding: 0 10px;
      margin-top: 10px;
      margin-bottom: 10px;
      width: 25% !important;
      max-width: 25% !important;

      &:first-child {
        margin-left: 0;
      }

      &:last-child {
        margin-right: 0;
      }
    }

    & .input-raw-100 {
      width: 100% !important;
      max-width: 100% !important;
    }

    & .hide {
      display: none !important;
    }

    & .break {
      flex-basis: 100%;
      width: 100% !important;
      max-width: 100% !important;
    }
  }

  & .section-block {
    margin: 10px 0;
    width: 100%;

    &__title {
      flex-basis: 100%;
      width: 100%;
      margin-bottom: 15px;
      color: $color-base;
      font-size: $font-size-middle-level-1;
    }
  }

  & .step-name {
    margin-bottom: 40px;
    font-weight: 600;
    font-size: 20px;
  }
}
</style>
